import { useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from "react";
import { api, type SessionSummary, type UsageReport } from "./api";
import { Dialog, ErrorMessage, Loading } from "./components";
import { count, money, pct, timestamp, tokens } from "./format";
import { dailyByModel, heatGrid, modelSeries, stackColumns } from "./homeCharts";
import { SessionView } from "./SessionView";
import { duration, roundedTop } from "./timeline";

const dayLabel = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const weekday = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
const hourLabel = (h: number) => (h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`);

function useWidth<T extends HTMLElement>(min = 240) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(min, Math.floor(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return { ref, width };
}

/** 1. The headline: what was spent, and what the premium-model share could have cost instead. */
export function SavingsHero({ r, days, onSeeSessions }: { r: UsageReport; days: number; onSeeSessions: () => void }) {
  const q = useQuery({ queryKey: ["savings", days], queryFn: () => api.savings(days) });
  const t = r.totals;
  const worth = q.data ? q.data.rows.filter((x) => x.spend - x.alternativeCost >= 0.01) : [];
  const top = worth[0];
  return (
    <forge-card className="hero">
      <div className="hero__grid">
        <div className="stack gap-xsmall">
          <span className="hero__eyebrow">Last {days} days</span>
          <span className="hero__figure">{money(t.spend)}</span>
          <span className="muted">
            {count(t.requests)} requests{t.failed > 0 ? ` · ${count(t.failed)} failed` : ""} · estimated at list prices
          </span>
        </div>
        <div className="hero__advice stack gap-xsmall">
          {q.isPending ? <Loading /> : q.error ? <ErrorMessage error={q.error} /> : top && q.data.potential >= 0.5 ? (
            <>
              <span className="hero__eyebrow"><forge-icon name="piggy_bank" /> What you could save</span>
              <span className="hero__advice-figure">up to {money(q.data.potential)} less</span>
              <span className="forge-typography--body2">
                if the {worth.map((x) => x.model).join(" and ")} work had run on {[...new Set(worth.map((x) => x.alternative))].join(" or ")}:{" "}
                {top.model} cost {money(top.spend)}, and the same tokens on {top.alternative} about {money(top.alternativeCost)}.
              </span>
              <span className="forge-typography--body2 muted">
                Only you know which work needed the larger model; your costliest sessions show where it went.{" "}
                <button type="button" className="link-button" onClick={onSeeSessions}>See them</button>
              </span>
            </>
          ) : (
            <>
              <span className="hero__eyebrow"><forge-icon name="check_circle" /> Model choice</span>
              <span className="forge-typography--body2">
                {q.data.premiumSpend > 0
                  ? "Your premium-model use is small; nothing big to save by switching."
                  : "No premium models in this period: your spend is already on cost-effective models."}
              </span>
            </>
          )}
        </div>
      </div>
    </forge-card>
  );
}

/** 4. What caching saved: LiteLLM's own figure, net of the cache-write premium. */
export function CacheCard({ r }: { r: UsageReport }) {
  const t = r.totals;
  const share = t.promptTokens > 0 ? t.cacheReadTokens / t.promptTokens : 0;
  return (
    <forge-card>
      <div className="stack gap-xsmall">
        <span className="hero__eyebrow"><forge-icon name="history" /> Prompt caching</span>
        <span className="stat-figure">{t.cacheSavings > 0 ? `saved you ${money(t.cacheSavings)}` : "saved nothing yet"}</span>
        <span className="forge-typography--body2">{pct(share)} of your input was read from the cache instead of sent at full price.</span>
        {t.gatewayCacheSavings > 0 && (
          <span className="forge-typography--body2 muted">{money(t.gatewayCacheSavings)} of that came from the gateway adding cache points for you.</span>
        )}
        {share < 0.5 && t.promptTokens > 100_000 && (
          <span className="forge-typography--body2 muted">
            Caching works when each request starts the same way as the last: long sessions in one tool, stable instructions at the top.
          </span>
        )}
      </div>
    </forge-card>
  );
}

/** 3. Spend per day, stacked by model. One axis; the By model table below is its table view. */
export function ModelMixChart({ r }: { r: UsageReport }) {
  const { ref, width } = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const days = dailyByModel(r);
  const series = modelSeries(r);
  const H = 180;
  const g = stackColumns(days, series, H);
  const band = width / Math.max(1, days.length);
  const barW = Math.max(2, Math.min(24, band - 2));
  const color = new Map(series.map((s) => [s.key, s.color]));
  const tip = hover === null ? null : g.columns[hover]!;
  return (
    <forge-card>
      <div className="row spread wrap gap-small">
        <h2 className="forge-typography--heading2 card-title">Spend per day, by model</h2>
        <div className="timeline__legend" aria-hidden="true">
          {series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}
        </div>
      </div>
      <div className="timeline" ref={ref}>
        <svg width={width} height={H + 22} role="img" className="timeline__svg"
          aria-label={`Spend per day from ${r.start} to ${r.end}, peak ${money(g.max)}. The By model table below lists the same totals.`}>
          <line x1={0} x2={width} y1={H} y2={H} className="timeline__baseline" />
          {g.columns.map((c, i) => {
            const x = i * band + (band - barW) / 2;
            const top = c.segments[c.segments.length - 1];
            return (
              <g key={c.date} className="timeline__bar" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={i * band} y={0} width={band} height={H} className="timeline__hit" />
                {c.segments.map((s) => s === top
                  ? <path key={s.key} d={roundedTop(x, s.y, barW, s.h)} fill={color.get(s.key)} />
                  : <rect key={s.key} x={x} y={s.y} width={barW} height={s.h} fill={color.get(s.key)} />)}
              </g>
            );
          })}
          <g className="timeline__labels" aria-hidden="true">
            <text x={0} y={H + 16} className="timeline__panel-label">{dayLabel(r.start)}</text>
            <text x={width} y={H + 16} textAnchor="end" className="timeline__panel-label">{dayLabel(r.end)}</text>
            <text x={0} y={10} className="timeline__panel-label">peak {money(g.max)}</text>
          </g>
        </svg>
        {tip && (
          <div className="timeline__tip" role="status" style={{ left: Math.min(Math.max(0, hover! * band - 100), width - 220) }}>
            <b>{weekday(tip.date)} {dayLabel(tip.date)} · {money(tip.total)}</b>
            <span>{count(tip.requests)} requests</span>
            {[...tip.segments].reverse().map((s) => <span key={s.key}>{series.find((x) => x.key === s.key)!.label}: {money(s.value)}</span>)}
          </div>
        )}
      </div>
    </forge-card>
  );
}

const BADGE_ICON: Record<string, string> = { saving: "piggy_bank", tip: "lightbulb", warning: "warning" };

/** 2. The costliest sessions of the week, each with what to look at; a row opens the session view. */
export function TopSessions() {
  const q = useQuery({ queryKey: ["top-sessions"], queryFn: api.topSessions });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const [open, setOpen] = useState<SessionSummary | null>(null);
  return (
    <forge-card id="top-sessions">
      <h2 className="forge-typography--heading2 card-title">Your costliest sessions this week</h2>
      {q.isPending ? <Loading /> : q.error ? <ErrorMessage error={q.error} /> : q.data.length === 0 ? (
        <p className="muted forge-typography--body2">No sessions in the last 7 days.</p>
      ) : (
        <div className="table-wrap">
          <table className="forge-data-table top-sessions">
            <thead>
              <tr><th>Session</th><th className="num">Cost (est.)</th><th className="num">Requests</th><th className="num">Length</th>
                <th className="num">Largest context</th><th>What to look at</th></tr>
            </thead>
            <tbody>
              {q.data.map((t) => {
                const s = t.session;
                const span = s.firstActivity ? Date.parse(s.lastActivity) - Date.parse(s.firstActivity) : 0;
                const openIt = () => setOpen(s);
                return (
                  <tr key={`${s.sessionId}|${s.keyId}`} className="clickable" tabIndex={0} onClick={openIt}
                    onKeyDown={(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openIt(); } }}
                    aria-label={`Session costing ${money(s.spend)}, open it`}>
                    <td><b>{s.tool ?? "unknown tool"}</b><div className="muted forge-typography--caption">{timestamp(s.lastActivity)} · {s.models.join(", ")}</div></td>
                    <td className="num"><b>{money(s.spend)}</b></td>
                    <td className="num">{count(s.requests)}</td>
                    <td className="num">{duration(span)}</td>
                    <td className="num">{tokens(t.largestContext)}</td>
                    <td>
                      <div className="badges">
                        {t.badges.length === 0 ? <span className="muted">looks efficient</span> : t.badges.map((b) => (
                          <span key={b.id} className={`badge badge--${b.kind}`}><forge-icon name={BADGE_ICON[b.kind] ?? "info"} />{b.label}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!open} onClose={() => setOpen(null)} label="Session" preset="right-sheet" wide>
        {open && <SessionView session={open} canReview={!!me.data?.sessionReview && open.userId === me.data.user.userId} />}
        <div><forge-button onClick={() => setOpen(null)}>Close</forge-button></div>
      </Dialog>
    </forge-card>
  );
}

/** 6. When the work happened, hour by hour, in the viewer's time zone. Long quiet runs are where caches expire. */
export function ActivityHeatmap() {
  const q = useQuery({ queryKey: ["activity"], queryFn: api.activity });
  const [hover, setHover] = useState<string | null>(null);
  if (q.isPending) return <forge-card><Loading /></forge-card>;
  if (q.error) return <ErrorMessage error={q.error} />;
  const g = heatGrid(q.data.buckets, 7);
  const cell = hover ? g.cells.get(hover) : null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <forge-card>
      <div className="row spread wrap gap-small">
        <h2 className="forge-typography--heading2 card-title">When you worked, last 7 days</h2>
        <span className="heatmap__key muted forge-typography--caption" aria-hidden="true">
          {tz} · fewer {[0, 1, 2, 3, 4].map((i) => <i key={i} className={`heat-${i}`} />)} more requests
        </span>
      </div>
      <div className="heatmap" role="img" aria-label={`Requests per hour over the last 7 days; busiest hour ${count(g.max)} requests`}>
        <div />
        {Array.from({ length: 24 }, (_, h) => <div key={h} className="heatmap__hour">{h % 3 === 0 ? hourLabel(h) : ""}</div>)}
        {g.rows.map((d) => (
          <div key={d} className="heatmap__row">
            <div className="heatmap__day">{weekday(d)} {dayLabel(d)}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const c = g.cells.get(`${d}|${h}`)!;
              return <div key={h} className={`heatmap__cell heat-${c.step}`} onMouseEnter={() => setHover(`${d}|${h}`)} onMouseLeave={() => setHover(null)} />;
            })}
          </div>
        ))}
      </div>
      <p className="muted forge-typography--body2 heatmap__status" aria-live="polite">
        {cell ? `${weekday(cell.day)} ${hourLabel(cell.hour)}: ${count(cell.requests)} requests, ${money(cell.spend)}`
          : "A pause longer than the cache lifetime (5 minutes, or 1 hour for the gateway's own cache points) makes the next request rebuild the cache."}
      </p>
      {q.data.truncated && (
        <p className="muted forge-typography--caption">Showing your latest {count(q.data.requestsRead)} of {count(q.data.total)} requests.</p>
      )}
    </forge-card>
  );
}
